import { AnimatePresence, motion } from 'framer-motion'
import { useState } from 'react'
import {
  Calendar,
  Check,
  Cpu,
  Users,
  Wrench,
  Zap,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react'

type FlowStep = {
  icon: LucideIcon
  title: string
  bullets: string[]
}

type Flow = {
  label: string
  shortLabel: string
  steps: FlowStep[]
}

const flows: Flow[] = [
  {
    label: 'Communities & Academies',
    shortLabel: 'Communities',
    steps: [
      {
        icon: Calendar,
        title: 'Set Up',
        bullets: [
          'Play in a smart court or use our portable setup',
          'No filming crew or complex setup needed',
          'Quick and easy to get started',
        ],
      },
      {
        icon: Cpu,
        title: 'Capture & Process',
        bullets: [
          'Games are recorded automatically',
          'Stats and highlights are created for each player',
          'Everything is prepared inside Statbox',
        ],
      },
      {
        icon: Users,
        title: 'Engage',
        bullets: [
          'Players receive replays, stats, and highlights',
          'Best moments are easy to share',
          'Your members stay more engaged over time',
        ],
      },
    ],
  },
  {
    label: 'Venues',
    shortLabel: 'Venues',
    steps: [
      {
        icon: Wrench,
        title: 'Install',
        bullets: [
          'We install and calibrate the system for your courts',
          'Setup is fast and simple',
          'No heavy operational effort from your team',
        ],
      },
      {
        icon: Zap,
        title: 'Activate',
        bullets: [
          'Every booking gets free replay access',
          'Stats and highlights become premium upsells',
          'The workflow runs with minimal staff involvement',
        ],
      },
      {
        icon: TrendingUp,
        title: 'Grow',
        bullets: [
          'Offer a more premium experience at your venue',
          'Generate recurring upsell revenue',
          'Stand out and bring players back more often',
        ],
      },
    ],
  },
]

/* ---------------- Step Card ---------------- */

const stepsContainerVariants = {
  hidden: { opacity: 0, y: 12 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.25,
      ease: [0.16, 1, 0.3, 1],
      staggerChildren: 0.07,
      delayChildren: 0.05,
    },
  },
  exit: {
    opacity: 0,
    y: -12,
    transition: { duration: 0.22, ease: 'easeOut' },
  },
}

const stepCardVariants = {
  hidden: { opacity: 0, y: 18 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] },
  },
}

const StepCard = ({
  step,
  stepIndex,
  flowKey,
}: {
  step: FlowStep
  stepIndex: number
  flowKey: string
}) => {
  const Icon = step.icon
  const stepNum = String(stepIndex + 1).padStart(2, '0')

  return (
    <motion.div
      variants={stepCardVariants}
      className="group relative h-full"
    >
      {/* Soft ambient glow behind card on hover */}
      <div
        aria-hidden
        className="pointer-events-none absolute -inset-2 rounded-[34px] opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{
          background:
            'radial-gradient(closest-side at 50% 0%, rgba(113,205,217,0.22) 0%, transparent 70%)',
          filter: 'blur(28px)',
        }}
      />

      <div
        className="relative flex h-full flex-col overflow-hidden rounded-[28px] border border-white/[0.07] p-7 transition-[border-color,transform,box-shadow] duration-500 ease-out group-hover:-translate-y-1 group-hover:border-white/[0.12] lg:p-8"
        style={{
          background:
            'linear-gradient(180deg, rgba(255,255,255,0.045) 0%, rgba(255,255,255,0.012) 60%, rgba(255,255,255,0) 100%), radial-gradient(closest-side at 100% 0%, rgba(113,205,217,0.10) 0%, transparent 70%)',
          boxShadow:
            'inset 0 1px 0 rgba(255,255,255,0.06), 0 30px 80px rgba(0,0,0,0.32)',
        }}
      >
        {/* Top sheen */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-7 top-0 h-px"
          style={{
            background:
              'linear-gradient(90deg, transparent 0%, rgba(113,205,217,0.55) 50%, transparent 100%)',
          }}
        />

        {/* Giant background step number */}
        <div
          aria-hidden
          className="pointer-events-none absolute -right-2 -top-6 select-none leading-none lg:-right-4 lg:-top-8"
          style={{
            fontFamily: 'Plus Jakarta Sans, system-ui, sans-serif',
            fontSize: 196,
            fontWeight: 800,
            color: 'rgba(113,205,217,0.06)',
            letterSpacing: '-0.06em',
          }}
        >
          {stepNum}
        </div>

        {/* Step pill */}
        <div className="relative">
          <span
            className="inline-flex items-center gap-2 rounded-full border border-[#71cdd9]/25 bg-[#71cdd9]/[0.06] px-3 py-1"
            style={{
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: 10.5,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.18em',
              color: '#71cdd9',
            }}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#71cdd9] shadow-[0_0_6px_#71cdd9]" />
            Step {stepNum}
          </span>
        </div>

        {/* Icon */}
        <div className="relative mt-7 inline-flex shrink-0 self-start">
          <div
            aria-hidden
            className="absolute -inset-3 rounded-[24px] opacity-90"
            style={{
              background:
                'radial-gradient(closest-side, rgba(113,205,217,0.32) 0%, transparent 72%)',
              filter: 'blur(18px)',
            }}
          />
          <div
            className="relative flex h-[64px] w-[64px] items-center justify-center rounded-[18px] border border-white/[0.10]"
            style={{
              background:
                'linear-gradient(160deg, rgba(113,205,217,0.20) 0%, rgba(91,83,180,0.16) 60%, rgba(255,255,255,0.02) 100%)',
              boxShadow:
                'inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -10px 22px rgba(0,0,0,0.22), 0 14px 30px rgba(113,205,217,0.18)',
            }}
          >
            <Icon
              className="text-[#71cdd9]"
              width={28}
              height={28}
              strokeWidth={1.6}
              aria-hidden
            />
          </div>
        </div>

        {/* Title */}
        <h3
          className="relative mt-7 text-[26px] font-bold leading-[1.15] tracking-tight text-white sm:text-[28px]"
          style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
        >
          {step.title}
        </h3>

        {/* Divider */}
        <div
          aria-hidden
          className="relative mt-5 h-px w-full"
          style={{
            background:
              'linear-gradient(90deg, rgba(113,205,217,0.22) 0%, rgba(255,255,255,0.04) 60%, transparent 100%)',
          }}
        />

        {/* Bullets */}
        <ul className="relative mt-5 flex flex-1 flex-col gap-3.5">
          {step.bullets.map((bullet, idx) => (
            <li
              key={`${flowKey}-${step.title}-bullet-${idx}`}
              className="flex items-start gap-3"
            >
              <span
                className="mt-[3px] flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-md"
                style={{
                  background:
                    'linear-gradient(180deg, rgba(113,205,217,0.22) 0%, rgba(113,205,217,0.08) 100%)',
                  border: '1px solid rgba(113,205,217,0.30)',
                  boxShadow: '0 0 8px rgba(113,205,217,0.18)',
                }}
              >
                <Check
                  size={11}
                  strokeWidth={2.5}
                  className="text-[#71cdd9]"
                  aria-hidden
                />
              </span>
              <span
                className="text-[14.5px] leading-[1.6] text-white/70"
                style={{ fontFamily: '"Open Sans", system-ui, sans-serif' }}
              >
                {bullet}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  )
}

/* ---------------- Audience toggle ---------------- */

const AudienceToggle = ({
  activeFlow,
  setActiveFlow,
}: {
  activeFlow: number
  setActiveFlow: (flow: number) => void
}) => (
  <div className="relative mx-auto w-full max-w-[540px]">
    {/* Ambient glow behind toggle */}
    <div
      aria-hidden
      className="pointer-events-none absolute -inset-4 rounded-full opacity-70"
      style={{
        background:
          'radial-gradient(closest-side, rgba(113,205,217,0.22) 0%, transparent 70%)',
        filter: 'blur(28px)',
      }}
    />

    <div className="relative rounded-2xl border border-white/[0.08] bg-[#0a0b0e]/90 p-1.5 backdrop-blur-xl sm:rounded-full">
      <div className="grid grid-cols-2 gap-1">
        {flows.map((flow, index) => {
          const active = activeFlow === index
          return (
            <button
              key={flow.label}
              type="button"
              aria-label={flow.label}
              aria-pressed={active}
              onClick={() => setActiveFlow(index)}
              className={[
                'relative z-10 flex min-h-[44px] w-full min-w-0 items-center justify-center rounded-xl px-2 py-2.5 text-center transition-colors duration-200 ease-out sm:rounded-full sm:px-4 sm:py-3',
                active
                  ? 'text-black font-semibold'
                  : 'text-white/55 font-medium hover:text-white/85',
              ].join(' ')}
            >
              {active && (
                <motion.div
                  layoutId="how-it-works-audience-pill"
                  className="absolute inset-0 rounded-xl sm:rounded-full"
                  style={{
                    background:
                      'linear-gradient(135deg, #71cdd9 0%, #b5eaf2 100%)',
                    boxShadow:
                      'inset 0 1px 0 rgba(255,255,255,0.4), 0 8px 24px rgba(113,205,217,0.30)',
                  }}
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                />
              )}
              <span className="relative z-10 text-[11px] font-semibold leading-snug sm:text-sm">
                <span className="sm:hidden">{flow.shortLabel}</span>
                <span className="hidden sm:inline">{flow.label}</span>
              </span>
            </button>
          )
        })}
      </div>
    </div>
  </div>
)

/* ---------------- Section ---------------- */

export const HowItWorks = () => {
  const [activeFlow, setActiveFlow] = useState(0)
  const flow = flows[activeFlow]

  return (
    <section
      id="how-it-works"
      className="relative overflow-hidden bg-[#07080a] py-20 md:py-28"
    >
      {/* Section ambient backdrop */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(circle at 50% 12%, rgba(113, 205, 217, 0.14) 0%, rgba(58, 83, 164, 0.06) 34%, transparent 60%), linear-gradient(180deg, #050607 0%, #0b0d10 48%, #050607 100%)',
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
          opacity: 0.55,
        }}
      />

      <div className="relative mx-auto flex w-full max-w-[1240px] flex-col items-center px-5 sm:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="text-center"
        >
          <span
            className="inline-flex items-center gap-2 rounded-full border border-[#71cdd9]/25 bg-[#71cdd9]/[0.06] px-3.5 py-1.5"
            style={{
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.18em',
              color: '#71cdd9',
            }}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#71cdd9] shadow-[0_0_6px_#71cdd9]" />
            How it works
          </span>

          <h2 className="mt-5 text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] text-white">
            From setup to{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-300 via-cyan-200 to-emerald-200">
              active players in days
            </span>
            .
          </h2>
          <p
            className="mx-auto mt-5 max-w-xl text-base sm:text-lg text-white/55"
            style={{ fontFamily: '"Open Sans", system-ui, sans-serif' }}
          >
            A clean three-step path that fits the way you already operate.
          </p>
        </motion.div>

        {/* Audience toggle */}
        <div className="mt-9 w-full md:mt-11">
          <AudienceToggle
            activeFlow={activeFlow}
            setActiveFlow={setActiveFlow}
          />
        </div>

        {/* Steps */}
        <div className="relative mt-12 w-full md:mt-16">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={flow.label}
              variants={stepsContainerVariants}
              initial="hidden"
              animate="show"
              exit="exit"
              className="grid grid-cols-1 gap-5 md:grid-cols-3 lg:gap-6"
            >
              {flow.steps.map((step, index) => (
                <StepCard
                  key={`${flow.label}-${step.title}`}
                  step={step}
                  stepIndex={index}
                  flowKey={flow.label}
                />
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  )
}
